import { Panel } from '@/ui/atoms/panel/Panel'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/ui/atoms/table/Table'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ColumnDefinition, FilterOptions, MobileViewOptions } from '@/ui/molecules/data-table/types'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import { useHydrated } from '@/ui/utils/useHydrated'
import { getStaggerStyle } from '@/ui/utils/staggeredEntrance'
import { Fragment, cloneElement, useMemo, useState } from 'react'
import { DataTable } from '../../molecules/data-table/DataTable'
import { CollapsibleCell } from './components/CollapsibleCell'
import { ResponsiveDataTableLoadingSkeleton } from './components/ResponsiveDataTableLoadingSkeleton'

export interface ResponsiveDataTableProps<T, F> {
  columnDefinition: { [key: string]: ColumnDefinition<T> }
  scroll?: {
    height: number
  }
  hideTableHeader?: boolean
  gridTemplateColumnsClassName?: string
  data: T[]
  'data-testid'?: string
  variant?: 'isolated-pairs' | 'isolated-positions' | 'default'
  footer?: React.ReactNode
  searchOptions?: {
    filterColumnKey: string
    placeholder: string
  }
  mobileFilterFn?: (data: T, filterValue: string) => boolean
  emptyStateText?: string
  withPanel?: boolean
  wrapperClassName?: string
  filter?: FilterOptions<T, F>
  mobileRowAction?: (row: T) => React.ReactNode
  disableCollapse?: boolean
}

interface DefinitionGroups<T> {
  collapsedDefinitions: ColumnDefinition<T>[]
  visibleDefinitions: ColumnDefinition<T>[]
}

function groupDefinitionsByVisibility<T>(definitions: ColumnDefinition<T>[]): DefinitionGroups<T> {
  return definitions.reduce(
    (acc, def) => {
      if (!def.showOnMobile) {
        acc.collapsedDefinitions.push(def)
      } else {
        acc.visibleDefinitions.push(def)
      }
      return acc
    },
    { collapsedDefinitions: [], visibleDefinitions: [] } as DefinitionGroups<T>,
  )
}

export function ResponsiveDataTable<T extends { [k: string]: any }, F>({
  columnDefinition,
  data,
  scroll,
  gridTemplateColumnsClassName,
  hideTableHeader = false,
  'data-testid': dataTestId,
  variant = 'default',
  footer,
  searchOptions,
  mobileFilterFn,
  emptyStateText,
  withPanel,
  wrapperClassName,
  filter,
  mobileRowAction,
  disableCollapse = false,
}: ResponsiveDataTableProps<T, F>) {
  // Wait for hydration before choosing mobile/desktop layout to avoid cross-layout flashing.
  const desktopBreakpoint = variant === 'isolated-pairs' ? 'lg' : 'md'
  const hydrated = useHydrated()
  const desktop = useBreakpoint(desktopBreakpoint)
  const [mobileSearchQuery, setMobileSearchQuery] = useState('')
  const [filterValue, setFilterValue] = useState<F | undefined>(filter?.initialState)

  const filteredData = useMemo(() => {
    if (!filter || filterValue === undefined || !filter.isActive(filterValue)) {
      return data
    }
    return data.filter((item) => filter.filterFn(item, filterValue))
  }, [data, filterValue, filter])

  if (!hydrated) {
    return (
      <ResponsiveDataTableLoadingSkeleton
        desktopRowCount={6}
        mobileRowCount={4}
        showFilterBar={Boolean(filter || mobileFilterFn)}
        desktopBreakpoint={desktopBreakpoint}
      />
    )
  }

  if (desktop) {
    return (
      <DataTable
        columnDef={columnDefinition}
        data={data}
        scroll={scroll}
        gridTemplateColumnsClassName={gridTemplateColumnsClassName}
        hideTableHeader={hideTableHeader}
        data-testid={dataTestId}
        variant={variant}
        footer={footer}
        searchOptions={searchOptions}
        emptyStateText={emptyStateText}
        withPanel={withPanel}
        wrapperClassName={wrapperClassName}
        filter={filter}
      />
    )
  }

  const [rowHeaderDefinition, ...contentDefinitions] = Object.entries(columnDefinition).flatMap(([key, def]) =>
    key !== searchOptions?.filterColumnKey ? [def] : [],
  )

  const { collapsedDefinitions, visibleDefinitions } = groupDefinitionsByVisibility(contentDefinitions)

  const visibleDefs = [rowHeaderDefinition, ...visibleDefinitions]

  const noHiddenColumns = collapsedDefinitions.length === 0
  const PanelWrapper = withPanel ? Panel.Wrapper : Fragment
  const FilterComponent = filter?.component

  return (
    <div>
      {(filter || mobileFilterFn) && (
        <div className="mb-6 flex flex-col gap-4">
          {FilterComponent &&
            cloneElement(FilterComponent, {
              value: filterValue,
              onChange: setFilterValue,
            })}

          {mobileFilterFn && (
            <div className="sm:ml-auto sm:max-w-sm">
              <SearchInput
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                placeholder={searchOptions?.placeholder ?? 'Search...'}
                className="py-4"
              />
            </div>
          )}
        </div>
      )}

      <PanelWrapper {...(withPanel ? { className: cn('flex flex-col gap-5 p-4 pt-2 lg:p-8', wrapperClassName) } : {})}>
        <Table data-testid={dataTestId} className="w-full table-fixed">
          {!hideTableHeader && (
            <TableHeader className="hidden">
              <TableRow className="flex justify-between">
                {visibleDefs.map((def, index) => (
                  <TableHead
                    className={cn(
                      'flex-1 py-2 text-foreground text-xs',
                      index !== 0 && 'flex px-2',
                      index !== 0 && def?.headerAlign === 'right' && 'justify-end',
                      index !== 0 && def?.headerAlign === 'center' && 'justify-center',
                      index !== 0 && !def?.headerAlign && 'justify-center',
                    )}
                    key={index}
                  >
                    {def?.header ?? ''}
                  </TableHead>
                ))}
                {!noHiddenColumns && <TableHead className="py-2 text-foreground text-xs">More</TableHead>}
              </TableRow>
            </TableHeader>
          )}
          <TableBody className="flex w-full flex-col gap-3">
            {filteredData.length ? (
              filteredData.map((value, index) => (
                <TableRow
                  key={index}
                  data-testid={testIds.component.DataTable.row(index)}
                  className="block w-full animate-stagger-in border-0"
                  style={getStaggerStyle(index)}
                >
                  <CollapsibleCell
                    noHiddenColumns={noHiddenColumns || disableCollapse}
                    rowAction={mobileRowAction?.(value)}
                  >
                    {[rowHeaderDefinition, ...visibleDefinitions].map((def, index) => (
                      <div className={cn(index === 0 ? 'flex-1' : 'shrink-0')} key={index}>
                        {def?.renderCell(value, {
                          isMobileView: true,
                          rowTitle: def.header,
                          showOnMobile: def.showOnMobile,
                        })}
                      </div>
                    ))}
                    {!disableCollapse &&
                      collapsedDefinitions.map((def, index) => {
                        const content = def.renderCell(value, { isMobileView: true, rowTitle: def.header })
                        if (!content) return null
                        return <Fragment key={index}>{content}</Fragment>
                      })}
                  </CollapsibleCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="pt-6 text-center">
                  <Typography variant="prompt" className="text-base">
                    {emptyStateText || 'No data to display'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {footer && <TableFooter>{footer}</TableFooter>}
        </Table>
      </PanelWrapper>
    </div>
  )
}

/**
 * Reusable helper for rendering a label-value row inside a ResponsiveDataTable
 * collapsible mobile card. Shows the label on the left and value on the right,
 * or centers the value when `showOnMobile` is true (header row style).
 */
export function MobileRow({
  label,
  children,
  mobileViewOptions,
}: {
  label: React.ReactNode
  children: React.ReactNode
  mobileViewOptions?: MobileViewOptions
}) {
  return (
    <div
      className={cn(
        'flex flex-row items-center',
        mobileViewOptions?.showOnMobile ? 'justify-center' : 'justify-between',
      )}
    >
      {!mobileViewOptions?.showOnMobile && (
        <Typography variant="prompt" className="w-full">
          {label}
        </Typography>
      )}
      <div className="shrink-0 text-right">{children}</div>
    </div>
  )
}
